import React, { useCallback, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { Checkbox } from '@atlaskit/checkbox';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { SelectedType } from '@atlaskit/tabs/types';

import { Translations as TranslationsType } from '../../../interfaces';
import {
    COPY_ID_TO_CLIPBOARD_TEXT,
    DEFAULT_VALUE_CHANGED,
} from '../../../utils/constants';
import Flag, { addSuccessFlag } from '../../common/flag/Flag';
import { FlagItem } from '../../common/flag/interfaces';
import Translations from '../Translations';

import {
    OptionIdText,
    OptionIdWrapper,
    TabsWrapper,
    TabWrapper,
} from './OptionsAdderStyled';
import { OptionsAdderValuesChanged} from './types';
import { handleIdToClipboardCopy } from './utils';

type OptionItemTabsProperties = {
    optionKey: string,
    isDefaultOption: boolean,
    onChange: OptionsAdderValuesChanged,
    optionIndex: number,
    optionValue: string,
    translation: Partial<TranslationsType>,
    locale: string,
    provided: DraggableProvided
}

const OptionItemTabs: React.FC<OptionItemTabsProperties> = ({
    optionKey,
    isDefaultOption,
    onChange,
    optionIndex,
    optionValue,
    translation,
    locale,
    provided
}) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [flags, setFlags] = useState<FlagItem[]>([]);

    const handleClipboardCopy = (id: string) => {
        handleIdToClipboardCopy(id);
        setFlags(addSuccessFlag(flags, COPY_ID_TO_CLIPBOARD_TEXT));
    };

    const renderGeneralConfiguration = (key: string) => (
        <TabWrapper>
            <Checkbox
                isChecked={isDefaultOption}
                label='Set as default'
                onChange={() => onChange(!isDefaultOption, DEFAULT_VALUE_CHANGED, optionIndex, optionKey)}
                name='checkbox-default'
            />
            <OptionIdWrapper>
                <div>{key}</div>
                <div className='copy-icon' onClick={() => handleClipboardCopy(key)}><CopyIcon label='Copy option ID' /></div>
            </OptionIdWrapper>
            <OptionIdText>Option ID</OptionIdText>
        </TabWrapper>
    );

    const renderTranslations = () => (
        <Translations
            translation={translation}
            originalValue={optionValue}
            optionIndex={optionIndex}
            locale={locale}
            onChange={onChange}
            provided={provided}
        />
    );

    const handleUpdate = useCallback(
        (index: SelectedType) => setSelectedTab(index),
        [setSelectedTab],
    );

    return (
        <TabsWrapper>
            <Flag flags={flags} setFlags={setFlags} />
            <Tabs  onChange={handleUpdate} selected={selectedTab}>
                <TabList>
                    <Tab>General configuration</Tab>
                    <Tab>Translations</Tab>
                </TabList>
                <TabPanel>
                    {renderGeneralConfiguration(optionKey)}
                </TabPanel>
                <TabPanel>
                    { renderTranslations()}
                </TabPanel>
            </Tabs>
        </TabsWrapper>
    );
};

export default OptionItemTabs;
